import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axiosInstance from '../api/axiosConfig';

export interface AuthUser {
  id: string;
  username: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  isAdmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  signup: (payload: { name: string; email: string; phone: string; company?: string; password: string }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    axiosInstance.get('/auth/me')
      .then((response) => setUser(response.data))
      .catch(() => {
        localStorage.removeItem('token');
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const response = await axiosInstance.post('/auth/login', { username, password });
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
  }, []);

  const signup = useCallback(async (payload: { name: string; email: string; phone: string; company?: string; password: string }) => {
    const response = await axiosInstance.post('/auth/signup', payload);
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
